import { useCallback, useEffect, useRef, useState } from "react";
import {
  RecaptchaVerifier,
  signInWithCustomToken,
  signInWithPhoneNumber,
} from "firebase/auth";
import { auth } from "../firebase";
import {
  checkFallbackOtp,
  signInWithPhoneIdToken,
  startFallbackOtp,
} from "../Services/studentAuth";

/**
 * The phone-OTP engine, shared by every surface that needs a verified number.
 *
 * WHY THIS IS A HOOK AND NOT COPY-PASTE
 * -------------------------------------
 * Two places now need this: the standalone sign-in card (PhoneSignIn) and the
 * end-of-test gate (PhoneOtpGate). The reCAPTCHA lifecycle below is the part
 * that took the longest to get right — a verifier left bound to a reused DOM
 * node makes Firebase throw "reCAPTCHA has already been rendered in this
 * element", which turns one failed attempt into a permanently broken form
 * until the page is reloaded. A second hand-copied version of that would drift
 * from this one and quietly reintroduce the bug, so there is exactly one.
 *
 * The hook owns: the code/verify state machine, the reCAPTCHA lifecycle, the
 * six-box keyboard behaviour, the cooldown, and the Firebase-code-to-sentence
 * mapping. It owns NO markup and no phone-number field — callers supply the
 * E.164 number and render whatever UI suits them.
 */

export const OTP_LENGTH = 6;
export const RESEND_SECONDS = 60;

/**
 * SMS FALLBACK (Twilio Verify, via the backend)
 * ---------------------------------------------
 * Firebase's own SMS route refuses whole carriers: every STC Kuwait number
 * (+9655…) fails from any device with `503 / "Error code: 39"`, while Zain
 * (+9659…) and Ooredoo (+9656…) deliver fine. Nothing in the Firebase console
 * changes that. So there are two ways into the fallback:
 *
 *  1. PRE-EMPTIVE — numbers matching FALLBACK_PREFIXES skip Firebase entirely.
 *     A refused Firebase attempt takes ~10 s to fail, so for a carrier known to
 *     be refused it is pure waiting. Override with
 *     REACT_APP_OTP_FALLBACK_PREFIXES (comma-separated E.164 prefixes; set it
 *     to "none" to always try Firebase first).
 *  2. REACTIVE — any other number that Firebase refuses to text
 *     (`auth/error-code:-NN`) is retried through the fallback automatically.
 *
 * Either way the student sees one form and gets one text. On verification the
 * backend returns a Firebase CUSTOM token for the same phone identity, so the
 * Ulearn session exchange below is byte-identical to the native path.
 */
const FALLBACK_PREFIXES = (() => {
  const raw = process.env.REACT_APP_OTP_FALLBACK_PREFIXES;
  if (raw === "none") return [];
  return (raw || "+9655")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
})();

export const prefersFallback = (e164) =>
  FALLBACK_PREFIXES.some((prefix) => String(e164 || "").startsWith(prefix));

/** Firebase declined to SEND the SMS (as opposed to rejecting the request). */
const isSmsRefusal = (error) =>
  /^auth\/error-code:/.test((error && error.code) || "");

export const PHONE_OTP_COPY = {
  ar: {
    title: "تسجيل الدخول",
    numberLabel: "رقم الهاتف",
    numberHint: "سنرسل لك رمزاً برسالة نصية للتأكد من رقمك.",
    send: "إرسال الرمز",
    sending: "جارٍ الإرسال…",
    otpLabel: "رمز التحقق",
    otpHint: `أدخل الرمز المكوّن من ${OTP_LENGTH} أرقام المُرسل إلى`,
    verify: "تأكيد وتسجيل الدخول",
    verifying: "جارٍ التحقق…",
    change: "تغيير الرقم",
    resend: "إعادة إرسال الرمز",
    resendIn: (s) => `يمكنك إعادة الإرسال بعد ${s} ثانية`,
    resent: "تم إرسال رمز جديد.",
    /* end-of-test gate */
    gateTitle: "أكّد رقمك لعرض نتيجتك",
    gateWhy: "نرسل رمزاً برسالة نصية للتأكد من رقمك، ثم نحفظ نتيجتك في حسابك.",
    gateVerify: "تأكيد وعرض النتيجة",
    gateSending: "جارٍ إرسال الرمز…",
    gateBack: "تعديل البيانات",
    errors: {
      "auth/invalid-phone-number":
        "رقم الهاتف غير صحيح. تأكد من الرقم ورمز الدولة.",
      "auth/missing-phone-number": "الرجاء إدخال رقم هاتفك.",
      "auth/invalid-verification-code":
        "الرمز غير صحيح. تحقق منه وحاول مرة أخرى.",
      "auth/code-expired": "انتهت صلاحية الرمز. اطلب رمزاً جديداً.",
      "auth/too-many-requests": "محاولات كثيرة. انتظر قليلاً ثم حاول مرة أخرى.",
      "auth/quota-exceeded": "الخدمة مشغولة حالياً. حاول مرة أخرى بعد قليل.",
      "auth/captcha-check-failed":
        "تعذّر التحقق من المتصفح. حدّث الصفحة وحاول مرة أخرى.",
      "auth/network-request-failed":
        "تعذّر الاتصال. تحقق من الإنترنت وحاول مرة أخرى.",
      "auth/operation-not-allowed": "تسجيل الدخول بالهاتف غير مفعّل حالياً.",
      "auth/user-disabled": "تم إيقاف هذا الحساب. تواصل مع الدعم.",
      "auth/invalid-app-credential":
        "تعذّر التحقق من التطبيق. حدّث الصفحة وحاول مرة أخرى.",
      "auth/app-not-authorized": "هذا الموقع غير مصرّح له بإرسال الرموز.",
      "auth/billing-not-enabled": "خدمة الرسائل غير متاحة حالياً.",
      "auth/internal-error": "حدث خطأ غير متوقع. حاول مرة أخرى.",
      "auth/argument-error": "حدث خطأ غير متوقع. حاول مرة أخرى.",
      short: "الرجاء إدخال رقم هاتف صحيح.",
      otpShort: `الرجاء إدخال الرمز المكوّن من ${OTP_LENGTH} أرقام.`,
      exchange: "تم التحقق من رقمك لكن تعذّر إنشاء الجلسة. حاول مرة أخرى.",
      exchangeDown:
        "تم التحقق من رقمك، لكن الخدمة غير متاحة حالياً. حاول لاحقاً.",
      smsUndeliverable:
        "تعذّر إرسال رمز إلى هذا الرقم حالياً. جرّب رقماً آخر أو تواصل معنا.",
      fallbackTooMany:
        "تم إرسال رموز كثيرة إلى هذا الرقم. انتظر قليلاً ثم حاول مرة أخرى.",
      generic: "تعذر تسجيل الدخول. حاول مرة أخرى.",
      unconfigured: "تسجيل الدخول غير مفعّل حالياً.",
    },
  },
  en: {
    title: "Sign in",
    numberLabel: "Phone number",
    numberHint: "We'll text you a code to confirm it's your number.",
    send: "Send code",
    sending: "Sending…",
    otpLabel: "Verification code",
    otpHint: `Enter the ${OTP_LENGTH}-digit code sent to`,
    verify: "Verify and sign in",
    verifying: "Verifying…",
    change: "Change number",
    resend: "Resend code",
    resendIn: (s) => `You can resend in ${s}s`,
    resent: "A new code is on its way.",
    /* end-of-test gate */
    gateTitle: "Confirm your number to see your result",
    gateWhy:
      "We'll text you a code to confirm it's your number, then save your result to your account.",
    gateVerify: "Verify and see my result",
    gateSending: "Sending your code…",
    gateBack: "Edit my details",
    errors: {
      "auth/invalid-phone-number":
        "That phone number doesn't look right. Check the number and country code.",
      "auth/missing-phone-number": "Please enter your phone number.",
      "auth/invalid-verification-code":
        "That code isn't right. Check it and try again.",
      "auth/code-expired": "That code has expired. Ask for a new one.",
      "auth/too-many-requests":
        "Too many attempts. Please wait a little and try again.",
      "auth/quota-exceeded":
        "The service is busy right now. Please try again shortly.",
      "auth/captcha-check-failed":
        "We couldn't verify your browser. Refresh the page and try again.",
      "auth/network-request-failed":
        "Couldn't reach the network. Check your connection and try again.",
      "auth/operation-not-allowed": "Phone sign-in isn't enabled right now.",
      "auth/user-disabled":
        "This account has been disabled. Please contact support.",
      "auth/invalid-app-credential":
        "We couldn't verify this app. Refresh the page and try again.",
      "auth/app-not-authorized": "This site isn't authorised to send codes.",
      "auth/billing-not-enabled": "Text messaging isn't available right now.",
      "auth/internal-error": "Something went wrong. Please try again.",
      "auth/argument-error": "Something went wrong. Please try again.",
      short: "Please enter a valid phone number.",
      otpShort: `Please enter the ${OTP_LENGTH}-digit code.`,
      exchange:
        "Your number was verified but we couldn't start your session. Please try again.",
      exchangeDown:
        "Your number was verified, but the service is unavailable right now. Please try again later.",
      smsUndeliverable:
        "We can't send a code to this number right now. Try another number, or contact us.",
      fallbackTooMany:
        "Too many codes were sent to this number. Please wait a little and try again.",
      generic: "Sign-in failed. Please try again.",
      unconfigured: "Sign-in isn't available right now.",
    },
  },
};

/**
 * Firebase error codes are stable identifiers, not sentences — never show one
 * to a student. Anything unmapped falls back to a plain apology.
 */
export const messageFor = (t, error) => {
  const code = (error && error.code) || "";
  if (t.errors[code]) return t.errors[code];

  /* Identity Toolkit refusals that Firebase does not map to a named code come
     through as `auth/error-code:-NN` — e.g. -39, which is Google declining to
     send the SMS (regional quota or abuse protection). "Sign-in failed, please
     try again" invites a student to retry something that will never succeed;
     this at least tells them the number is the problem, not their typing. */
  if (/^auth\/error-code:/.test(code)) return t.errors.smsUndeliverable;
  // Some SDK failures arrive with no `code` at all — the reCAPTCHA lifecycle
  // errors in particular ("reCAPTCHA has already been rendered in this
  // element"). Fall back to matching the one stable word in the message so
  // these read as a verification problem rather than a blank apology.
  const message = (error && error.message) || "";
  if (message.includes("reCAPTCHA"))
    return t.errors["auth/captcha-check-failed"];
  return t.errors.generic;
};

/**
 * Firebase parses the Identity Toolkit response, maps it to one of its own
 * codes, and throws away the body — so the console shows a bare "400 (Bad
 * Request)" and `auth/invalid-app-credential`, which covers half a dozen very
 * different causes (INVALID_APP_CREDENTIAL, CAPTCHA_CHECK_FAILED,
 * MISSING_RECAPTCHA_TOKEN, OPERATION_NOT_ALLOWED, BILLING_NOT_ENABLED…).
 *
 * This clones any failed identitytoolkit response and prints the real reason.
 * It reads a CLONE, so the SDK still gets its untouched body, and it swallows
 * its own errors so logging can never break sign-in. Installed once, lazily.
 */
let fetchLoggerInstalled = false;
const installIdentityToolkitLogger = () => {
  if (fetchLoggerInstalled || typeof window === "undefined" || !window.fetch)
    return;
  fetchLoggerInstalled = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await original(input, init);
    try {
      const url = typeof input === "string" ? input : input && input.url;
      if (
        url &&
        url.includes("identitytoolkit.googleapis.com") &&
        !response.ok
      ) {
        const body = await response.clone().text();
        // eslint-disable-next-line no-console
        console.error(
          `[PhoneSignIn] Identity Toolkit rejected the request (HTTP ${response.status}):`,
          body,
        );
      }
    } catch {
      /* diagnostics must never interfere with authentication */
    }
    return response;
  };
};

/**
 * A hint that fits the failure.
 *
 * This used to print one fixed checklist — "Phone provider enabled, this
 * origin in Authorised domains, and project billing" — on EVERY failure. That
 * is actively misleading: if the provider were disabled or the origin were
 * unauthorised, every number would fail, so when one country fails and another
 * works those two can be ruled out by inspection. Sending someone to check
 * them wastes their time on a settings page that cannot be the cause.
 */
const hintFor = (error) => {
  const code = (error && error.code) || "";
  if (auth && auth.settings && auth.settings.appVerificationDisabledForTesting) {
    return "TEST MODE is on — only fictional numbers registered in the Firebase console will work.";
  }
  if (/^auth\/error-code:/.test(code)) {
    return (
      "Identity Toolkit refused to SEND the SMS — the request itself was accepted, " +
      "so the number, the app verifier and the authorised domain are all fine. " +
      "This is Google declining delivery: regional SMS quota or abuse protection. " +
      "If other countries work and this one does not, it is the destination country, " +
      "not the app. Check the project's billing plan, and SMS region policy if the " +
      "project has the Identity Platform upgrade."
    );
  }
  if (code === "auth/captcha-check-failed" || code === "auth/invalid-app-credential") {
    return "reCAPTCHA/app verification. Check the reCAPTCHA key configuration and that this origin is an authorised domain.";
  }
  if (code === "auth/operation-not-allowed") {
    return "The Phone provider is disabled for this Firebase project.";
  }
  if (code === "auth/too-many-requests" || code === "auth/quota-exceeded") {
    return "Rate limited or out of quota for this project. Wait, or check the billing plan.";
  }
  return "Check: Phone provider enabled, this origin in Authorised domains, and project billing.";
};

const logDiagnostics = (stage, error) => {
  const server =
    (error && error.customData && error.customData._tokenResponse) ||
    (error && error._tokenResponse) ||
    null;
  // eslint-disable-next-line no-console
  console.error(`[PhoneSignIn] ${stage} failed`, {
    code: (error && error.code) || "(none)",
    message: (error && error.message) || String(error),
    serverReason:
      (server &&
        server.error &&
        (server.error.message || server.error.status)) ||
      "(not exposed)",
    testMode: Boolean(
      auth && auth.settings && auth.settings.appVerificationDisabledForTesting,
    ),
    origin:
      typeof window !== "undefined" ? window.location.origin : "(no window)",
    hint: hintFor(error),
  });
};

/**
 * @param {Object}   options
 * @param {string}   options.language  "ar" | "en"
 * @param {Function} options.onVerified fired with {student, claimed} once the
 *                   number is confirmed AND the Ulearn session exists.
 */
export const usePhoneOtp = ({ language = "ar", onVerified } = {}) => {
  const ar = language === "ar";
  const t = PHONE_OTP_COPY[language] || PHONE_OTP_COPY.ar;

  const [step, setStep] = useState("number"); // number | otp
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const confirmationRef = useRef(null);
  // "firebase" | "twilio" — which channel texted the code in flight. Decides
  // how verifyCode() turns the six digits into a Firebase user.
  const channelRef = useRef("firebase");
  const fallbackPhoneRef = useRef(""); // E.164 the Twilio code was sent to
  const verifierRef = useRef(null);
  const verifierNodeRef = useRef(null); // the throwaway div this verifier owns
  const recaptchaHostRef = useRef(null);
  const otpBoxesRef = useRef([]); // one <input> per digit
  const mountedRef = useRef(true);

  /** Move focus to box `index`, if it exists. */
  const focusBox = (index) => {
    const box = otpBoxesRef.current[index];
    if (box) {
      box.focus();
      box.select();
    }
  };

  /**
   * Write one digit into `index` and advance.
   *
   * The boxes are always indexed 0..5 in logical order — box 0 is the FIRST
   * digit of the code. In Arabic the row is laid out right-to-left by the
   * container's direction, so box 0 renders on the right and typing flows the
   * way an Arabic reader expects, with no index maths here.
   */
  const setDigit = (index, digit) => {
    setOtp((prev) => {
      const next = prev.padEnd(OTP_LENGTH, " ").split("");
      next[index] = digit || " ";
      return next.join("").trimEnd();
    });
    setError("");
  };

  const handleOtpChange = (index) => (event) => {
    const typed = event.target.value.replace(/\D/g, "");
    if (!typed) {
      setDigit(index, "");
      return;
    }
    if (typed.length > 1) {
      // a paste (or a fast typist) landed in one box — spread it forward
      const chars = typed.slice(0, OTP_LENGTH - index).split("");
      setOtp((prev) => {
        const next = prev.padEnd(OTP_LENGTH, " ").split("");
        chars.forEach((c, offset) => {
          next[index + offset] = c;
        });
        return next.join("").trimEnd();
      });
      setError("");
      focusBox(Math.min(index + chars.length, OTP_LENGTH - 1));
      return;
    }
    setDigit(index, typed);
    if (index < OTP_LENGTH - 1) focusBox(index + 1);
  };

  const handleOtpKeyDown = (index) => (event) => {
    if (event.key === "Backspace") {
      event.preventDefault();
      if (otp[index] && otp[index] !== " ") {
        setDigit(index, "");
      } else if (index > 0) {
        setDigit(index - 1, "");
        focusBox(index - 1);
      }
      return;
    }
    // Arrow keys follow the VISUAL order, which is mirrored in Arabic
    const back = ar ? "ArrowRight" : "ArrowLeft";
    const forward = ar ? "ArrowLeft" : "ArrowRight";
    if (event.key === back && index > 0) {
      event.preventDefault();
      focusBox(index - 1);
    } else if (event.key === forward && index < OTP_LENGTH - 1) {
      event.preventDefault();
      focusBox(index + 1);
    }
  };

  /** A paste anywhere in the row fills the whole code from box 0. */
  const handleOtpPaste = (event) => {
    const text =
      (event.clipboardData || window.clipboardData).getData("text") || "";
    const digits = text.replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!digits) return;
    event.preventDefault();
    setOtp(digits);
    setError("");
    focusBox(Math.min(digits.length, OTP_LENGTH - 1));
  };

  // `otp` can hold a gap (a cleared middle box is stored as a space), so the
  // submit gate counts DIGITS rather than string length — otherwise "1 3456"
  // would look complete at six characters and enable an invalid submit.
  const otpDigits = otp.replace(/\D/g, "");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* ------------------------------------------------------ reCAPTCHA ------ */
  /**
   * Firebase requires an app verifier for phone auth. The invisible widget is
   * bound to a DOM node, so it MUST be torn down when this component unmounts:
   * a stale verifier left in place makes the next mount fail with
   * captcha-check-failed, which reads to the student as "sign-in is broken".
   */
  const clearVerifier = useCallback(() => {
    if (verifierRef.current) {
      try {
        verifierRef.current.clear();
      } catch {
        /* already torn down */
      }
      verifierRef.current = null;
    }
    // clear() resets Firebase's own state but grecaptcha leaves its widget in
    // the DOM. Removing the node is what actually frees the element.
    if (verifierNodeRef.current) {
      try {
        verifierNodeRef.current.remove();
      } catch {
        /* already detached */
      }
      verifierNodeRef.current = null;
    }
  }, []);

  const getVerifier = useCallback(() => {
    installIdentityToolkitLogger();
    if (verifierRef.current) return verifierRef.current;
    const host = recaptchaHostRef.current;
    if (!host) return null;

    // Every verifier gets a BRAND NEW element.
    //
    // Firebase throws "reCAPTCHA has already been rendered in this element"
    // when a widget — or the debris clear() leaves behind — is still in the
    // node. That matters more than it sounds: when the reCAPTCHA Enterprise
    // check fails, the SDK silently falls back to the v2 flow and re-renders
    // into this same container mid-request. Reusing one node therefore turns a
    // single failed attempt into a permanently broken form until reload.
    const mount = () => {
      const node = document.createElement("div");
      host.appendChild(node);
      verifierNodeRef.current = node;
      return new RecaptchaVerifier(auth, node, { size: "invisible" });
    };

    try {
      verifierRef.current = mount();
    } catch (error) {
      // last resort: wipe the host completely and mount once more
      logDiagnostics("recaptcha init", error);
      host.innerHTML = "";
      verifierRef.current = mount();
    }
    return verifierRef.current;
  }, []);

  useEffect(() => clearVerifier, [clearVerifier]);

  /* -------------------------------------------------------- cooldown ----- */
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  /* ---------------------------------------------------------- actions ---- */

  /**
   * Text a code to `e164`. The caller owns the number and any field-level
   * validation of it; this only refuses an obviously empty one.
   */
  const sendCode = async (e164, isResend = false) => {
    setError("");
    setNotice("");
    if (String(e164 || "").replace(/\D/g, "").length < 8) {
      setError(t.errors.short);
      return false;
    }
    setBusy(true);

    const codeIsOnItsWay = () => {
      setStep("otp");
      setOtp("");
      setCooldown(RESEND_SECONDS);
      if (isResend) setNotice(t.resent);
    };

    /**
     * Twilio path. Returns true when the code was sent. On failure it sets the
     * student-facing error itself and returns false — the backend's 4xx
     * messages are already written for a student, and its 503 (fallback not
     * configured) is the one case where "can't send to this number" is the
     * honest answer.
     */
    const sendViaFallback = async () => {
      try {
        await startFallbackOtp(e164);
        if (!mountedRef.current) return false;
        channelRef.current = "twilio";
        fallbackPhoneRef.current = e164;
        confirmationRef.current = null;
        codeIsOnItsWay();
        return true;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[PhoneSignIn] SMS fallback (Twilio) failed", {
          status: err && err.status,
          message: err && err.message,
        });
        if (!mountedRef.current) return false;
        if (err && err.status === 429) setError(t.errors.fallbackTooMany);
        else if (err && err.status === 400) setError(t.errors["auth/invalid-phone-number"]);
        else setError(t.errors.smsUndeliverable);
        return false;
      }
    };

    try {
      // carriers Firebase is known to refuse: don't make the student wait ~10 s
      // for the refusal, go straight to Twilio
      if (prefersFallback(e164)) return await sendViaFallback();

      const verifier = getVerifier();
      if (!verifier) throw new Error("no-verifier");
      const confirmation = await signInWithPhoneNumber(auth, e164, verifier);
      if (!mountedRef.current) return false;
      channelRef.current = "firebase";
      confirmationRef.current = confirmation;
      codeIsOnItsWay();
      return true;
    } catch (err) {
      logDiagnostics("send code", err);
      if (!mountedRef.current) return false;
      // a failed attempt burns the invisible widget; rebuild it next time or
      // every subsequent try fails for the wrong reason
      clearVerifier();
      // Firebase accepted the request but declined to text this number: the
      // number is fine, the route is not. Try the other route before giving up.
      if (isSmsRefusal(err)) return await sendViaFallback();
      setError(err && err.code ? messageFor(t, err) : t.errors.generic);
      return false;
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  const verifyCode = async () => {
    setError("");
    setNotice("");
    const code = otp.replace(/\D/g, "");
    if (code.length !== OTP_LENGTH) {
      setError(t.errors.otpShort);
      return;
    }
    const viaFallback = channelRef.current === "twilio";
    if (!viaFallback && !confirmationRef.current) {
      setError(t.errors["auth/code-expired"]);
      setStep("number");
      return;
    }
    setBusy(true);
    try {
      let credential;
      if (viaFallback) {
        // The backend checks the code with Twilio and, if it matches, mints a
        // custom token for the Firebase user that owns this number. Signing
        // in with it yields an ID token carrying `phone_number`, exactly like
        // the native path — so /auth/phone below needs no special case.
        let customToken;
        try {
          customToken = await checkFallbackOtp(fallbackPhoneRef.current, code);
        } catch (checkError) {
          // eslint-disable-next-line no-console
          console.error("[PhoneSignIn] fallback code check failed", {
            status: checkError && checkError.status,
            message: checkError && checkError.message,
          });
          if (!mountedRef.current) return;
          const status = checkError && checkError.status;
          if (status === 401) {
            // wrong code, or Twilio's 10-minute window has passed
            setError(
              /expired/i.test(checkError.message || "")
                ? t.errors["auth/code-expired"]
                : t.errors["auth/invalid-verification-code"],
            );
          } else if (status === 429) {
            setError(t.errors["auth/too-many-requests"]);
          } else if (status >= 500) {
            setError(t.errors.exchangeDown);
          } else {
            setError(t.errors.generic);
          }
          return;
        }
        credential = await signInWithCustomToken(auth, customToken);
      } else {
        credential = await confirmationRef.current.confirm(code);
      }
      const idToken = await credential.user.getIdToken();
      let result;
      try {
        result = await signInWithPhoneIdToken(idToken);
      } catch (exchangeError) {
        // the number IS verified; it's our backend that refused. Say so
        // precisely rather than blaming the code the student just typed.
        // eslint-disable-next-line no-console
        console.error("ulearn session exchange failed:", exchangeError);
        if (mountedRef.current) {
          setError(
            exchangeError && exchangeError.status >= 500
              ? t.errors.exchangeDown
              : t.errors.exchange,
          );
        }
        return;
      }
      if (!mountedRef.current) return;
      clearVerifier();
      if (onVerified) onVerified(result);
    } catch (err) {
      logDiagnostics("verify code", err);
      if (!mountedRef.current) return;
      setError(messageFor(t, err));
    } finally {
      if (mountedRef.current) setBusy(false);
    }
  };

  /** Back to the number step, with the in-flight confirmation discarded. */
  const reset = () => {
    confirmationRef.current = null;
    channelRef.current = "firebase";
    fallbackPhoneRef.current = "";
    clearVerifier();
    setOtp("");
    setError("");
    setNotice("");
    setStep("number");
  };

  return {
    t,
    ar,
    step,
    setStep,
    otp,
    otpDigits,
    busy,
    error,
    setError,
    notice,
    setNotice,
    cooldown,
    otpBoxesRef,
    recaptchaHostRef,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    sendCode,
    verifyCode,
    reset,
    clearVerifier,
  };
};

export default usePhoneOtp;
